import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { Wallet } from 'lucide-react';
import { useLanguage } from '@/hooks/useTranslation';
import { CountUp, EASE_OUT } from './motion-primitives';

type T = (key: any) => string;

/** Patrimoine mensuel affiché dans la démo (données d'exemple). */
const MONTHLY_WEALTH = [
  21300, 22800, 22100, 24600, 24000, 26900, 26200, 29400, 28700, 31500, 33200, 35450,
];
const CYCLE_MS = 5200;
const CHART_MAX = Math.max(...MONTHLY_WEALTH) * 1.12;

/** Courbe lissée (bézier symétrique) à partir de points normalisés. */
function buildPath(values: number[], width: number, height: number) {
  const step = width / (values.length - 1);
  const points = values.map((v, i) => [i * step, height - (v / CHART_MAX) * height] as const);

  return points.reduce((path, [x, y], i) => {
    if (i === 0) return `M ${x} ${y}`;
    const [px, py] = points[i - 1];
    const cx = (px + x) / 2;
    return `${path} C ${cx} ${py}, ${cx} ${y}, ${x} ${y}`;
  }, '');
}

const leftPct = (i: number) => (i / (MONTHLY_WEALTH.length - 1)) * 100;
const topPct = (value: number) => (1 - value / CHART_MAX) * 100;
const formatAmount = (value: number, locale: string) => value.toLocaleString(locale);

function OverviewPanel({ t, locale }: { t: T; locale: string }) {
  const width = 640;
  const height = 190;
  const line = buildPath(MONTHLY_WEALTH, width, height);
  const area = `${line} L ${width} ${height} L 0 ${height} Z`;
  const reduce = useReducedMotion();
  const plotRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState<number | null>(null);

  const months = Array.from({ length: 12 }, (_, i) =>
    new Intl.DateTimeFormat(locale, { month: 'short' }).format(new Date(2025, i, 1))
  );

  const pickPoint = (clientX: number) => {
    const rect = plotRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    setHovered(Math.round(ratio * (MONTHLY_WEALTH.length - 1)));
  };

  const current = hovered === null ? MONTHLY_WEALTH.length - 1 : hovered;
  const previous = MONTHLY_WEALTH[current - 1];
  const delta = previous ? ((MONTHLY_WEALTH[current] - previous) / previous) * 100 : 2.4;

  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500">
        {t('home.mock.totalWealth')}
      </p>
      <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <p className="text-4xl font-semibold tracking-tight text-white sm:text-5xl">
          {hovered === null ? (
            <CountUp to={MONTHLY_WEALTH[MONTHLY_WEALTH.length - 1]} />
          ) : (
            <span className="tabular-nums">{formatAmount(MONTHLY_WEALTH[hovered], locale)}</span>
          )}{' '}
          <span className="text-2xl text-gray-500 sm:text-3xl">€</span>
        </p>
        <p className="text-sm text-gray-400">
          <span className={`font-medium ${delta >= 0 ? 'text-[#16c47f]' : 'text-red-400'}`}>
            {delta >= 0 ? '+' : ''}
            {delta.toFixed(1).replace('.', ',')} %
          </span>{' '}
          {hovered === null ? t('home.mock.thisMonth') : months[current]}
        </p>
      </div>

      <div
        ref={plotRef}
        onPointerMove={(event) => pickPoint(event.clientX)}
        onPointerDown={(event) => pickPoint(event.clientX)}
        onPointerLeave={() => setHovered(null)}
        className="relative mt-8 cursor-crosshair touch-pan-y"
      >
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="h-40 w-full sm:h-48"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="clevia-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#16c47f" stopOpacity="0.32" />
              <stop offset="100%" stopColor="#16c47f" stopOpacity="0" />
            </linearGradient>
          </defs>

          {[0.25, 0.5, 0.75].map((ratio) => (
            <line
              key={ratio}
              x1="0"
              x2={width}
              y1={height * ratio}
              y2={height * ratio}
              stroke="white"
              strokeOpacity="0.06"
            />
          ))}

          <motion.path
            d={area}
            fill="url(#clevia-area)"
            initial={reduce ? undefined : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
          />
          <motion.path
            d={line}
            fill="none"
            stroke="#16c47f"
            strokeWidth="2.5"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            initial={reduce ? undefined : { pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.6, ease: EASE_OUT }}
          />
        </svg>

        {/* Repère de survol : les éléments sont en HTML pour ne pas subir
            la déformation du viewBox étiré (preserveAspectRatio="none"). */}
        {hovered !== null && (
          <>
            <div
              className="pointer-events-none absolute inset-y-0 w-px bg-white/20"
              style={{ left: `${leftPct(hovered)}%` }}
              aria-hidden="true"
            />
            <div
              className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-gray-900 bg-[#16c47f]"
              style={{ left: `${leftPct(hovered)}%`, top: `${topPct(MONTHLY_WEALTH[hovered])}%` }}
              aria-hidden="true"
            />
            <div
              className="pointer-events-none absolute -translate-y-[calc(100%+14px)] whitespace-nowrap rounded-md border border-white/10 bg-gray-950/95 px-2.5 py-1.5 text-xs shadow-lg"
              style={{
                left: `${leftPct(hovered)}%`,
                top: `${topPct(MONTHLY_WEALTH[hovered])}%`,
                transform:
                  hovered === 0
                    ? 'translate(0, calc(-100% - 14px))'
                    : hovered === MONTHLY_WEALTH.length - 1
                      ? 'translate(-100%, calc(-100% - 14px))'
                      : 'translate(-50%, calc(-100% - 14px))',
              }}
            >
              <span className="text-gray-400">{months[hovered]}</span>{' '}
              <span className="font-medium tabular-nums text-white">
                {formatAmount(MONTHLY_WEALTH[hovered], locale)} €
              </span>
            </div>
          </>
        )}

        <div className="mt-3 flex text-[10px] uppercase tracking-[0.18em] text-gray-600">
          {months.map((month, i) => (
            <span
              key={month}
              className={`flex-1 text-center transition-colors duration-150 ${
                hovered === i ? 'text-gray-300' : ''
              } ${i % 3 === 0 || hovered === i ? '' : 'invisible sm:visible'}`}
            >
              {month}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function AssetsPanel({ t }: { t: T }) {
  const assets = [
    {
      name: 'CIH Bank',
      value: '12 450',
      currency: 'MAD',
      pct: 35,
      logo: '/bank-logos/cihbank_ma.png',
    },
    {
      name: 'BNP Paribas',
      value: '8 750',
      currency: '€',
      pct: 25,
      logo: '/bank-logos/bnpparibasfortis_be.png',
    },
    { name: t('home.mock.asset.savings'), value: '5 600', currency: '€', pct: 16, logo: null },
    { name: t('home.mock.asset.crypto'), value: '3 650', currency: '€', pct: 10, logo: null },
  ];

  return (
    <div className="divide-y divide-white/5">
      {assets.map((asset, i) => (
        <motion.div
          key={asset.name}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: i * 0.07, ease: EASE_OUT }}
          className="flex items-center gap-4 py-4 first:pt-0 last:pb-0"
        >
          {asset.logo ? (
            <img src={asset.logo} alt="" className="h-9 w-9 shrink-0 rounded-lg object-cover" />
          ) : (
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/5">
              <Wallet className="h-4 w-4 text-gray-500" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">{asset.name}</p>
            <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-white/10">
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 0.15 + i * 0.07, ease: EASE_OUT }}
                style={{ width: `${asset.pct}%`, transformOrigin: 'left' }}
                className="h-full rounded-full bg-[#16c47f]"
              />
            </div>
          </div>
          <p className="shrink-0 text-sm font-medium tabular-nums text-white">
            {asset.value} {asset.currency}
          </p>
        </motion.div>
      ))}
    </div>
  );
}

function BudgetPanel({ t }: { t: T }) {
  const budgets = [
    { name: t('home.mock.budget.food'), spent: 320, max: 400 },
    { name: t('home.mock.budget.transport'), spent: 85, max: 150 },
    { name: t('home.mock.budget.shopping'), spent: 180, max: 200 },
    { name: t('home.mock.budget.leisure'), spent: 260, max: 250 },
  ];

  // Encodage tri-état : sain / attention / dépassement
  const tone = (ratio: number) =>
    ratio > 1 ? 'bg-red-500' : ratio > 0.85 ? 'bg-amber-400' : 'bg-[#16c47f]';

  return (
    <div className="space-y-5">
      <div className="flex items-baseline justify-between">
        <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500">
          {t('home.mock.monthlyBudget')}
        </p>
        <p className="text-sm font-medium tabular-nums text-white">2 000 €</p>
      </div>

      {budgets.map((cat, i) => {
        const ratio = cat.spent / cat.max;
        return (
          <motion.div
            key={cat.name}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: i * 0.07, ease: EASE_OUT }}
          >
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-gray-300">{cat.name}</span>
              <span className="tabular-nums text-gray-400">
                {cat.spent} / {cat.max} €
              </span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.07, ease: EASE_OUT }}
                style={{ width: `${Math.min(ratio, 1) * 100}%`, transformOrigin: 'left' }}
                className={`h-full rounded-full ${tone(ratio)}`}
              />
            </div>
            {ratio > 1 && (
              <p className="mt-1.5 text-xs text-red-400">
                {t('home.mock.overBudget')} {cat.spent - cat.max} €
              </p>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}

export function ProductPreview({ t }: { t: T }) {
  const [active, setActive] = useState(0);
  const { language } = useLanguage();
  const locale = language === 'en' ? 'en-US' : 'fr-FR';
  const [paused, setPaused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inView = useInView(containerRef, { margin: '-120px' });
  const reduce = useReducedMotion();

  const tabs = [t('home.mock.tab.overview'), t('home.mock.tab.assets'), t('home.mock.tab.budget')];

  // Démo qui se joue toute seule tant qu'elle est visible et non survolée
  useEffect(() => {
    if (!inView || paused || reduce) return;
    const timer = window.setTimeout(() => setActive((i) => (i + 1) % 3), CYCLE_MS);
    return () => window.clearTimeout(timer);
  }, [inView, paused, reduce, active]);

  return (
    <div
      ref={containerRef}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      className="overflow-hidden rounded-xl border border-white/10 bg-gray-900 shadow-[0_40px_120px_-40px_rgba(0,0,0,0.6)]"
    >
      <div className="flex items-center gap-6 border-b border-white/10 px-5 sm:px-8">
        {tabs.map((label, i) => (
          <button
            key={label}
            type="button"
            onClick={() => setActive(i)}
            className={`relative py-4 text-sm font-medium outline-none transition-colors duration-200 focus-visible:text-white ${
              active === i ? 'text-white' : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            {label}
            {active === i && (
              <motion.span
                layoutId="clevia-tab-underline"
                className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-[#16c47f]"
                transition={{ duration: 0.35, ease: EASE_OUT }}
              />
            )}
          </button>
        ))}
      </div>

      <div className="min-h-[340px] p-5 sm:min-h-[380px] sm:p-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: EASE_OUT }}
          >
            {active === 0 && <OverviewPanel t={t} locale={locale} />}
            {active === 1 && <AssetsPanel t={t} />}
            {active === 2 && <BudgetPanel t={t} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
